/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useCallback, ChangeEvent, HTMLAttributes, ReactElement } from 'react'
import classnames from 'classnames'
import { FieldHookConfig, useField } from 'formik'
import { omit } from 'lodash'
import { useTranslations } from 'next-intl'
import style from './Field.module.css'

type IFieldProps = {
  // В случае ошибки, поправить типизацию
  label: string | ReactElement
  className?: string
  inputClassName?: string
  errorClassName?: string
  hasSmallMarginBottom?: boolean
  isBlockShowError?: boolean
  wrapErrorText?: boolean
  formatter?: (value: string) => string
  autocomplete?: string
  errorDataQaLocator?: string
  onChangeHandler?(e: ChangeEvent<HTMLInputElement>): void
  onFocusHandler?(e: ChangeEvent<HTMLInputElement>): void
  onBlurHandler?(e: ChangeEvent<HTMLInputElement>): void
  errorRenderer?(error: string): ReactElement
} & FieldHookConfig<string>

export const Field: FC<IFieldProps> = ({
  label,
  hasSmallMarginBottom,
  formatter,
  errorDataQaLocator,
  onChangeHandler,
  onFocusHandler,
  onBlurHandler,
  errorRenderer,
  className,
  isBlockShowError,
  inputClassName,
  errorClassName,
  wrapErrorText,
  ...props
}) => {
  const [field, meta, helper] = useField(props)
  const isErrorMessageVisible = Boolean(meta.error && meta.touched)

  const classNames = classnames(style.field, className, {
    [style.smallMarginBottom]: hasSmallMarginBottom,
  })

  const inputClassNames = classnames(style.input, inputClassName, {
    [style.errorField]: isErrorMessageVisible,
  })

  const errorClassNames = classnames(errorClassName, {
    [style.errorMessage]: !isBlockShowError && isErrorMessageVisible,
    [style.errorMessageDefault]: !isErrorMessageVisible,
  })

  const t = useTranslations()

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (formatter) {
        e.target.value = formatter(e.target.value)
      }

      if (onChangeHandler) {
        onChangeHandler(e)
      }

      field.onChange(e)
    },
    [field, formatter, onChangeHandler],
  )

  const onBlur = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onBlurHandler) {
        onBlurHandler(e)
      }

      field.onBlur(e)
    },
    [field, onBlurHandler],
  )

  const onFocus = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onFocusHandler) {
        onFocusHandler(e)
      }

      helper.setTouched(false)
    },
    [helper, onFocusHandler],
  )

  const renderError = () => {
    if (meta.error && errorRenderer) {
      return errorRenderer(meta.error)
    }

    return meta.error ? t(meta.error) : ''
  }

  return (
    <div className={classNames}>
      <div>
        <label className={style.label}>{label}</label>
      </div>
      <input
        {...field}
        {...(omit(props, 'validate') as HTMLAttributes<HTMLInputElement>)}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={inputClassNames}
      />
      <div data-qa-locator={errorDataQaLocator} className={errorClassNames}>
        {!isBlockShowError && renderError()}
      </div>
    </div>
  )
}

Field.displayName = 'Field'
